var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { createElement as _createElement } from "react";
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import * as React from 'react';
import { classNames, responsiveClassNames, responsiveVariables } from "../../utils/helpers";
import { Hidden } from "../hidden";
import s from './Stack.module.css';
const StackItem = ({ size, grow, gap, as, order, children, className, attributes, }) => {
    const TagName = as || 'div';
    const itemClassNames = classNames(s.item, className, gap === 'auto' && s['--item-gap-auto'], ...responsiveClassNames(s, '--item-grow', grow), ...responsiveClassNames(s, '--item-size', size));
    const itemVariables = Object.assign(Object.assign({}, responsiveVariables('--st-stack-o', order)), responsiveVariables('--st-stack-gi', gap));
    return (_jsx(TagName, Object.assign({}, attributes, { className: itemClassNames, style: itemVariables, children: children })));
};
StackItem.displayName = 'Stack.Item';
export const Stack = ({ children, direction = 'column', align, justify, gap = 3, as, wrap, height, className, attributes, }) => {
    const TagName = as || 'div';
    // If wrap is not defined, it can be set based on item grow and split usage
    let nowrap;
    const renderItem = ({ className, child, index }) => {
        var _a, _b;
        const isItem = child.type === StackItem;
        const key = child.key || index;
        const dividerElement = null;
        let itemElement;
        if (isItem) {
            itemElement = React.cloneElement(child, {
                className: classNames(className, child.props.className),
            });
        }
        else if (className || !React.isValidElement(child)) {
            itemElement = (_jsx(StackItem, { className: className, children: child }, key));
        }
        else {
            itemElement = child;
        }
        // Passing grow here because it's responsive and nowrap should follow it
        if (isItem && ((_a = child.props) === null || _a === void 0 ? void 0 : _a.grow))
            nowrap = child.props.grow;
        if (isItem && ((_b = child.props) === null || _b === void 0 ? void 0 : _b.gap) === 'auto')
            nowrap = true;
        return [dividerElement, itemElement];
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const formattedChildren = React.Children.map(children, (child, index) => {
        if (!child)
            return null;
        if (child.type === Hidden && typeof child.props.children !== 'function') {
            const _a = child.props, { children: hiddenChild } = _a, hiddenProps = __rest(_a, ["children"]);
            const key = child.key || index;
            return (_createElement(Hidden, Object.assign({}, hiddenProps, { key: key }), className => renderItem({ className, child: hiddenChild, index })));
        }
        return renderItem({ child, index });
    });
    const rootClassNames = classNames(s.root, className, ...responsiveClassNames(s, '--direction', direction), ...responsiveClassNames(s, '--align', align), ...responsiveClassNames(s, '--justify', justify), 
    // Wrap and nowrap are separate here because inverting any of them could result into a false value which will be ignored by classNames
    ...responsiveClassNames(s, '--nowrap', nowrap || wrap === false), ...responsiveClassNames(s, '--wrap', wrap));
    const rootVariables = Object.assign(Object.assign({}, responsiveVariables('--st-stack-g', gap)), responsiveVariables('--st-stack-h', height));
    return (_jsx(TagName, Object.assign({ "data-testid": "st-stack" }, attributes, { className: rootClassNames, style: rootVariables, children: formattedChildren })));
};
Stack.Item = StackItem;
Stack.displayName = 'Stack';
